<script setup lang="ts">
import CloseIcon from '@assets/icons/close-round.svg?component';
import { watch, toRefs, onMounted, computed } from 'vue';

type Props = {
  visibility?: boolean
  zIndex?: number
  contentClass?: string
  closeButton?: boolean
  variant?: 'center' | 'panel'
};

const props = withDefaults(defineProps<Props>(), {
  zIndex: 9999,
  contentClass: '',
  closeButton: true,
  variant: 'center',
});

const emit = defineEmits<{
  'update:visibility': [value: boolean];
}>();

const { visibility, contentClass, closeButton } = toRefs(props);
const transitionName = computed(() => {
  return props.variant === 'panel' ? 'slide-right' : 'fade';
});
const variantClass = computed(() => {
  return props.variant === 'panel' ? 'base-dialog--panel' : '';
});

const onClose = (e: MouseEvent) => {
  const target = e.target as HTMLElement;

  document.body.style.overflow = 'visible';
  document.body.style.height = 'auto';

  if (window.getSelection()?.toString()) {
    return;
  }

  if (
    target?.closest('.base-dialog__content')
    && !target?.closest('.base-dialog__close-btn')
  ) {
    return;
  }

  emit('update:visibility', false);
};

const onEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    document.body.style.overflow = 'visible';
    document.body.style.height = 'auto';
    emit('update:visibility', false);
  }
};

watch(visibility, (value) => {
  if (typeof value === 'undefined') {
    return;
  }
  if (value) {
    window.addEventListener('keydown', onEscape);
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
  } else {
    window.removeEventListener('keydown', onEscape);
    document.body.style.overflow = 'visible';
    document.body.style.height = 'auto';
  }
});

onMounted(() => {
  if (visibility.value) {
    window.addEventListener('keydown', onEscape);
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
  }
});
</script>

<template>
  <Transition :name="transitionName">
    <div
      v-show="visibility"
      class="base-dialog"
      :class="variantClass"
      data-testpl="dialog-modal"
      :style="{zIndex}"
      @click="onClose"
    >
      <div
        :class="['base-dialog__content', contentClass]"
        data-testpl="dialog-modal-content"
      >
        <button
          v-if="closeButton"
          class="base-dialog__close-btn text-black hover:text-turquoise"
          type="button"
          data-testid="login-close-button"
          data-testpl="dialog-modal-close-button"
        >
          <CloseIcon class="size-24 lg:size-32" />
        </button>

        <slot />
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.base-dialog {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  width: 100%;
  height: 100%;
  padding: 16px;
  overflow: auto;
  background: rgba(#42BAC1, 0.6);
  justify-content: center;
  align-items: center;

  @include up('lg') {
    padding: 60px;
  }
}

.base-dialog--panel {
  padding: 0;
  overflow: hidden;
  background: rgba(9, 40, 42, 0.45); // neutral-ish dim backdrop
  justify-content: flex-end;
  align-items: stretch;
}

.base-dialog__content {
  position: relative;
  max-width: 800px;
  width: 100%;
  margin: auto;
  padding: 30px;
  background-color: var(--color-white);

  @include up('lg') {
    padding: 60px;
  }
}

.base-dialog--panel .base-dialog__content {
  max-width: 560px;
  height: 100%;
  margin: 0 0 0 auto;
  padding: 56px 20px 24px;
  overflow-y: auto;
  overflow-x: hidden;

  @include up('lg') {
    padding: 64px 28px 32px;
  }
}

.base-dialog__successes {
  max-width: 440px;
}

.base-dialog__close-btn {
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  padding: 16px;

  @include up('lg') {
    top: 24px;
    right: 24px;
  }
}

.base-dialog--panel .base-dialog__close-btn {
  top: 16px;
  right: 16px;
  padding: 0;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.dark-modal {
  background-color: var(--color-deep-blue);
  color: var(--color-white);

  @include up('lg') {
    max-width: 700px;
    padding: 70px 120px;
  }
}
</style>
